/** Local edit state and canonical API mapping for one modeling plan. */
import type { PlanOptionPayload } from '../../../../api/plan-options';
import type { PlanAlignerPricingMode, PlanOption, PlanScheme } from '../../../../types/admin';

export const PLAN_LIMITS = {
  jawAligners: 1000,
  alignerItemQuantity: 3000,
  estimatedStages: 1000,
  maxCourseAligners: 100_000,
  daysPerAligner: 365,
  durationDays: 10_000,
  blockQuantity: 10_000,
  presentationItems: 100,
  titleLength: 255,
  labelLength: 255,
  textLength: 4000,
  money: 9_999_999_999.99,
} as const;

export type DraftAlignerItem = {
  readonly id: string;
  readonly type: string;
  readonly kind: 'REGULAR' | 'WITH_OVERLAYS' | 'SETUP' | 'RETENTION_CAP' | 'CUSTOM';
  readonly jaw: 'upper' | 'lower' | 'none';
  readonly quantity: string;
  readonly unitPrice: string;
};
export type DraftClinicalElement = {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly paid: boolean;
  readonly price: string;
  readonly scheme: PlanScheme | null;
  readonly schemes: readonly PlanScheme[];
};
export type DraftDiscount = {
  readonly id: string;
  readonly name: string;
  readonly comment: string;
  readonly amount: string;
};

export type PlanDraft = {
  readonly title: string;
  readonly clinicalComment: string;
  readonly estimatedStages: string;
  readonly maxCourseAligners: string;
  readonly upperCount: string;
  readonly lowerCount: string;
  readonly setupAlignersCount: string;
  readonly daysPerAligner: string;
  readonly durationOverrideDays: string;
  readonly alignerPricingMode: PlanAlignerPricingMode;
  readonly standardUnitPrice: string;
  readonly alignerItems: readonly DraftAlignerItem[];
  readonly clinicalElements: readonly DraftClinicalElement[];
  readonly discounts: readonly DraftDiscount[];
  readonly depositAmount: string;
  readonly attachments: {
    readonly enabled: boolean;
    readonly type: 'Composite' | 'Negative' | 'Other';
    readonly quantity: string;
    readonly comment: string;
    readonly scheme: PlanScheme | null;
    readonly schemes: readonly PlanScheme[];
  };
  readonly buttons: {
    readonly enabled: boolean;
    readonly quantity: string;
    readonly type: string;
    readonly unitPrice: string;
    readonly scheme: PlanScheme | null;
    readonly schemes: readonly PlanScheme[];
  };
  readonly elastics: {
    readonly enabled: boolean;
    readonly sets: string;
    readonly type: string;
    readonly unitPrice: string;
    readonly scheme: PlanScheme | null;
    readonly schemes: readonly PlanScheme[];
  };
  readonly separation: {
    readonly enabled: boolean;
    readonly comment: string;
    readonly price: string;
    readonly scheme: PlanScheme | null;
    readonly schemes: readonly PlanScheme[];
  };
  readonly keyDifference: string;
};

export function nextDraftId(prefix = 'draft'): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return `${prefix}-${cryptoApi.randomUUID()}`;
  }
  if (typeof cryptoApi?.getRandomValues === 'function') {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    const token = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${prefix}-${token}`;
  }
  // Old/non-secure test runtimes only. Multiple entropy sources avoid a
  // repeat when two editor instances mount during the same millisecond.
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

export function parseCount(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

export function parseAmount(value: string): number {
  const trimmed = value.trim();
  if (!/^\d+(?:\.\d+)?$/.test(trimmed)) return 0;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : 0;
}

function money(value: string, fallback = '0'): string {
  return value.trim() || fallback;
}

function nullableText(value: string): string | null {
  return value.trim() || null;
}

export function toDraft(option: PlanOption): PlanDraft {
  const schemesFor = (block: { readonly scheme?: PlanScheme | null; readonly schemes?: readonly PlanScheme[] } | null | undefined) =>
    block?.schemes?.length ? block.schemes : block?.scheme ? [block.scheme] : [];

  return {
    title: option.title ?? '',
    clinicalComment: option.clinical_comment ?? '',
    estimatedStages: (option.estimated_stages ?? 1).toString(),
    maxCourseAligners: (option.max_course_aligners ?? 0).toString(),
    upperCount: option.upper_aligners_count?.toString() ?? '0',
    lowerCount: option.lower_aligners_count?.toString() ?? '0',
    setupAlignersCount: (option.setup_aligners_count ?? 0).toString(),
    daysPerAligner: (option.days_per_aligner ?? 10).toString(),
    durationOverrideDays: option.duration_override_days?.toString() ?? '',
    alignerPricingMode: option.aligner_pricing_mode ?? 'AUTO',
    standardUnitPrice: option.standard_unit_price ?? '2300',
    alignerItems: (option.aligner_items ?? []).map((item) => ({
      id: item.id,
      type: item.type,
      kind: item.kind ?? 'CUSTOM',
      jaw: item.jaw ?? 'none',
      quantity: item.quantity.toString(),
      unitPrice: item.unit_price,
    })),
    clinicalElements: (option.clinical_elements ?? []).map((element) => ({
      id: element.id,
      title: element.title,
      description: element.description ?? '',
      paid: element.paid,
      price: element.price ?? '0',
      scheme: element.scheme ?? null,
      schemes: schemesFor(element),
    })),
    discounts: (option.discounts ?? []).map((discount) => ({
      id: discount.id,
      name: discount.name,
      comment: discount.comment ?? '',
      amount: discount.amount,
    })),
    depositAmount: option.deposit_amount ?? '0',
    attachments: {
      enabled: option.attachments?.enabled ?? false,
      type: option.attachments?.type ?? 'Composite',
      quantity: option.attachments?.quantity?.toString() ?? '',
      comment: option.attachments?.comment ?? '',
      scheme: option.attachments?.scheme ?? null,
      schemes: schemesFor(option.attachments),
    },
    buttons: {
      enabled: option.buttons?.enabled ?? false,
      quantity: option.buttons?.quantity?.toString() ?? '',
      type: option.buttons?.type ?? '',
      unitPrice: option.buttons?.unit_price ?? '110',
      scheme: option.buttons?.scheme ?? null,
      schemes: schemesFor(option.buttons),
    },
    elastics: {
      enabled: option.elastics?.enabled ?? false,
      sets: option.elastics?.sets?.toString() ?? '',
      type: option.elastics?.type ?? '',
      unitPrice: option.elastics?.unit_price ?? '50',
      scheme: option.elastics?.scheme ?? null,
      schemes: schemesFor(option.elastics),
    },
    separation: {
      enabled: option.separation?.enabled ?? false,
      comment: option.separation?.comment ?? '',
      price: option.separation?.price ?? '1200',
      scheme: option.separation?.scheme ?? null,
      schemes: schemesFor(option.separation),
    },
    keyDifference: option.key_difference ?? '',
  };
}

export function manufacturedThisStage(draft: PlanDraft): number {
  return (
    (parseCount(draft.upperCount) ?? 0) +
    (parseCount(draft.lowerCount) ?? 0) +
    (parseCount(draft.setupAlignersCount) ?? 0)
  );
}

export function suggestedDurationDays(draft: PlanDraft): number {
  return (
    Math.max(parseCount(draft.upperCount) ?? 0, parseCount(draft.lowerCount) ?? 0) *
    (parseCount(draft.daysPerAligner) ?? 0)
  );
}

export function effectiveDurationDays(draft: PlanDraft): number {
  return parseCount(draft.durationOverrideDays) ?? suggestedDurationDays(draft);
}

export function customAllocatedCount(draft: PlanDraft): number {
  return draft.alignerItems.reduce(
    (sum, item) =>
      item.kind === 'RETENTION_CAP' ? sum : sum + (parseCount(item.quantity) ?? 0),
    0,
  );
}

export function retentionCapCount(draft: PlanDraft): number {
  return draft.alignerItems.reduce(
    (sum, item) =>
      item.kind === 'RETENTION_CAP' ? sum + (parseCount(item.quantity) ?? 0) : sum,
    0,
  );
}

export type PlanDraftIssue =
  | 'clinical_comment_required'
  | 'custom_quantity_mismatch'
  | 'integer_invalid'
  | 'value_out_of_range'
  | 'money_invalid'
  | 'text_too_long'
  | 'list_too_long'
  | 'required_label';

function isIntegerInRange(value: string, min: number, max: number, optional = false): boolean {
  if (optional && value.trim() === '') return true;
  const parsed = parseCount(value);
  return parsed !== null && parsed >= min && parsed <= max;
}

function isMoney(value: string): boolean {
  const trimmed = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(trimmed)) return false;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= PLAN_LIMITS.money;
}

export function validatePlanDraft(draft: PlanDraft): readonly PlanDraftIssue[] {
  const issues = new Set<PlanDraftIssue>();
  if (!draft.clinicalComment.trim()) issues.add('clinical_comment_required');
  if (
    draft.alignerPricingMode === 'CUSTOM' &&
    customAllocatedCount(draft) !== manufacturedThisStage(draft)
  ) {
    issues.add('custom_quantity_mismatch');
  }

  const integers: readonly (readonly [string, number, number, boolean?])[] = [
    [draft.estimatedStages, 1, PLAN_LIMITS.estimatedStages],
    [draft.maxCourseAligners, 0, PLAN_LIMITS.maxCourseAligners],
    [draft.upperCount, 0, PLAN_LIMITS.jawAligners],
    [draft.lowerCount, 0, PLAN_LIMITS.jawAligners],
    [draft.setupAlignersCount, 0, PLAN_LIMITS.jawAligners],
    [draft.daysPerAligner, 1, PLAN_LIMITS.daysPerAligner],
    [draft.durationOverrideDays, 0, PLAN_LIMITS.durationDays, true],
    ...(draft.attachments.enabled && draft.attachments.type === 'Composite'
      ? [[draft.attachments.quantity, 0, PLAN_LIMITS.blockQuantity, true] as const]
      : []),
    [draft.buttons.quantity, 0, PLAN_LIMITS.blockQuantity, true],
    [draft.elastics.sets, 0, PLAN_LIMITS.blockQuantity, true],
    ...draft.alignerItems.map(
      (item) => [item.quantity, 0, PLAN_LIMITS.alignerItemQuantity] as const,
    ),
  ];
  integers.forEach(([value, min, max, optional]) => {
    if (parseCount(value) === null && !(optional && value.trim() === '')) {
      issues.add('integer_invalid');
    } else if (!isIntegerInRange(value, min, max, optional)) {
      issues.add('value_out_of_range');
    }
  });

  const moneyValues = [
    draft.standardUnitPrice,
    draft.depositAmount,
    draft.buttons.unitPrice,
    draft.elastics.unitPrice,
    draft.separation.price,
    ...draft.alignerItems.map((item) => item.unitPrice),
    ...draft.clinicalElements.filter((element) => element.paid).map((element) => element.price),
    ...draft.discounts.map((discount) => discount.amount),
  ];
  if (moneyValues.some((value) => !isMoney(value))) issues.add('money_invalid');

  const longTexts: readonly (readonly [string, number])[] = [
    [draft.title, PLAN_LIMITS.titleLength],
    [draft.clinicalComment, PLAN_LIMITS.textLength],
    [draft.keyDifference, PLAN_LIMITS.textLength],
    [draft.attachments.comment, PLAN_LIMITS.textLength],
    [draft.buttons.type, PLAN_LIMITS.labelLength],
    [draft.elastics.type, PLAN_LIMITS.labelLength],
    [draft.separation.comment, PLAN_LIMITS.textLength],
    ...draft.alignerItems.map((item) => [item.type, PLAN_LIMITS.labelLength] as const),
    ...draft.clinicalElements.flatMap((element) => [
      [element.title, PLAN_LIMITS.labelLength] as const,
      [element.description, PLAN_LIMITS.textLength] as const,
    ]),
    ...draft.discounts.flatMap((discount) => [
      [discount.name, PLAN_LIMITS.labelLength] as const,
      [discount.comment, PLAN_LIMITS.textLength] as const,
    ]),
  ];
  if (longTexts.some(([value, max]) => value.length > max)) issues.add('text_too_long');

  if (
    draft.alignerItems.some((item) => !item.type.trim()) ||
    draft.clinicalElements.some((element) => !element.title.trim()) ||
    draft.discounts.some((discount) => !discount.name.trim())
  ) {
    issues.add('required_label');
  }
  if (
    draft.alignerItems.length > PLAN_LIMITS.presentationItems ||
    draft.clinicalElements.length > PLAN_LIMITS.presentationItems ||
    draft.discounts.length > PLAN_LIMITS.presentationItems
  ) {
    issues.add('list_too_long');
  }
  return [...issues];
}

export function draftToPayload(draft: PlanDraft): PlanOptionPayload {
  return {
    title: nullableText(draft.title),
    upper_aligners_count: parseCount(draft.upperCount),
    lower_aligners_count: parseCount(draft.lowerCount),
    clinical_comment: nullableText(draft.clinicalComment),
    estimated_stages: parseCount(draft.estimatedStages),
    max_course_aligners: parseCount(draft.maxCourseAligners),
    setup_aligners_count: parseCount(draft.setupAlignersCount),
    days_per_aligner: parseCount(draft.daysPerAligner),
    duration_override_days: parseCount(draft.durationOverrideDays),
    aligner_pricing_mode: draft.alignerPricingMode,
    standard_unit_price: money(draft.standardUnitPrice, '2300'),
    aligner_items: draft.alignerItems.map((item) => ({
      id: item.id,
      type: item.type.trim(),
      kind: item.kind,
      jaw: item.jaw === 'none' ? null : item.jaw,
      quantity: parseCount(item.quantity) ?? 0,
      unit_price: money(item.unitPrice),
    })),
    clinical_elements: draft.clinicalElements.map((element) => ({
      id: element.id,
      title: element.title.trim(),
      description: nullableText(element.description),
      paid: element.paid,
      price: element.paid ? money(element.price) : null,
    })),
    discounts: draft.discounts.map((discount) => ({
      id: discount.id,
      name: discount.name.trim(),
      comment: nullableText(discount.comment),
      amount: money(discount.amount),
    })),
    deposit_amount: money(draft.depositAmount),
    attachments: draft.attachments.enabled
      ? {
          enabled: true,
          type: draft.attachments.type,
          quantity:
            draft.attachments.type === 'Composite'
              ? parseCount(draft.attachments.quantity)
              : null,
          comment: nullableText(draft.attachments.comment),
        }
      : { enabled: false },
    buttons: draft.buttons.enabled
      ? {
          enabled: true,
          quantity: parseCount(draft.buttons.quantity),
          type: nullableText(draft.buttons.type),
          unit_price: money(draft.buttons.unitPrice, '110'),
        }
      : {
          enabled: false,
          unit_price: money(draft.buttons.unitPrice, '110'),
        },
    elastics: draft.elastics.enabled
      ? {
          enabled: true,
          sets: parseCount(draft.elastics.sets),
          type: nullableText(draft.elastics.type),
          unit_price: money(draft.elastics.unitPrice, '50'),
        }
      : {
          enabled: false,
          unit_price: money(draft.elastics.unitPrice, '50'),
        },
    separation: draft.separation.enabled
      ? {
          enabled: true,
          comment: nullableText(draft.separation.comment),
          price: money(draft.separation.price, '1200'),
        }
      : {
          enabled: false,
          price: money(draft.separation.price, '1200'),
        },
    key_difference: nullableText(draft.keyDifference),
  };
}

export function isDraftDirty(draft: PlanDraft, option: PlanOption): boolean {
  return JSON.stringify(draftToPayload(draft)) !== JSON.stringify(draftToPayload(toDraft(option)));
}
